import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from './Admin.module.css';
import { getAuthToken } from '../lib/auth';
import { HOST } from '../config/api';
import AutocompleteInput from './autocompleteinput/AutocompleteInput';
import { useCoachOnboarding } from './coach/onboarding/CoachOnboardingProvider';
import { COACH_STEPS } from './coach/onboarding/coachOnboardingSteps';
import { parseAiTrainingParameters } from '../features/coach/aiTrainingParameters';
import { getExerciseCategoryLabel } from '../utils/exerciseCategoryLabels';

const fallbackExerciseTypes = [
    { id: 'warmup', exerciseType: 'Warmup' },
    { id: 'a1', exerciseType: 'A1' },
    { id: 'a2', exerciseType: 'A2' },
    { id: 'tech', exerciseType: 'Technique' },
    { id: 'recovery', exerciseType: 'Recovery' },
    { id: 'cooldown', exerciseType: 'Cooldown' },
];

const emptyExercises = () => ([
    { category: '', description: '' },
    { category: '', description: '' },
    { category: '', description: '' },
]);

const emptyTrainingMetadata = () => ({
    discipline: null,
    phase: null,
    session_type: null,
    focus: null,
    estimated_duration_minutes: null,
    reason: null,
});

const emptyEditableTraining = () => ({
    id: null,
    name: '',
    exercises: [],
});

const SHOW_IMPORT_TRAINING_PANEL = false;

const normalizeTrainingForList = (training) => ({
    id: training?.id ?? null,
    name: training?.name || '',
    exercises: training?.exercises || [],
    locked: Boolean(training?.locked),
    timestamp: training?.timestamp || null,
});

const normalizeTrainingPayload = (parsedTraining, sourceLabel = 'Training') => {
    if (!parsedTraining?.name || typeof parsedTraining.name !== 'string') {
        throw new Error(`${sourceLabel} is missing a valid name.`);
    }

    if (!Array.isArray(parsedTraining?.exercises) || parsedTraining.exercises.length === 0) {
        throw new Error(`${sourceLabel} must contain at least one exercise.`);
    }

    const normalizedExercises = parsedTraining.exercises.map((exercise, index) => {
        const category = typeof exercise?.category === 'string' ? exercise.category.trim() : '';
        const description = typeof exercise?.description === 'string' ? exercise.description.trim() : '';

        if (!category || !description) {
            throw new Error(`Exercise ${index + 1} is missing category or description.`);
        }

        return { category, description };
    });

    return {
        name: parsedTraining.name.trim(),
        discipline: typeof parsedTraining.discipline === 'string' && parsedTraining.discipline.trim()
            ? parsedTraining.discipline.trim()
            : null,
        phase: parsedTraining.phase !== undefined && parsedTraining.phase !== null && String(parsedTraining.phase).trim()
            ? String(parsedTraining.phase).trim()
            : null,
        session_type: typeof parsedTraining.session_type === 'string' && parsedTraining.session_type.trim()
            ? parsedTraining.session_type.trim()
            : null,
        focus: typeof parsedTraining.focus === 'string' && parsedTraining.focus.trim()
            ? parsedTraining.focus.trim()
            : null,
        estimated_duration_minutes: parsedTraining.estimated_duration_minutes !== undefined
            && parsedTraining.estimated_duration_minutes !== null
            && Number.isFinite(Number(parsedTraining.estimated_duration_minutes))
            ? Number(parsedTraining.estimated_duration_minutes)
            : null,
        reason: typeof parsedTraining.reason === 'string' && parsedTraining.reason.trim()
            ? parsedTraining.reason.trim()
            : null,
        exercises: normalizedExercises,
    };
};

const decodeImportedTraining = (encodedTraining) => {
    const normalized = (encodedTraining || '').replace(/\s+/g, '');

    if (!normalized) {
        throw new Error('Paste a base64 training string first.');
    }

    let decodedString = '';

    try {
        const binary = window.atob(normalized);
        const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
        decodedString = new TextDecoder('utf-8').decode(bytes);
    } catch (error) {
        throw new Error('The pasted value is not valid base64.');
    }

    let parsedTraining;

    try {
        parsedTraining = JSON.parse(decodedString);
    } catch (error) {
        throw new Error('The decoded value is not valid JSON.');
    }

    return normalizeTrainingPayload(parsedTraining, 'Imported training');
};

const Admin = ({ userId, userInfo = null, demoMode = false, demoData = null }) => {
    const [newExercises, setNewExercises] = useState(emptyExercises());
    const [userHistory, setUserHistory] = useState([]);
    const [currentTraining, setCurrentTraining] = useState(undefined);
    const [currentTrainingName, setCurrentTrainingName] = useState('');
    const [currentTrainingId, setCurrentTrainingId] = useState(null);
    const [currentTrainingLocked, setCurrentTrainingLocked] = useState(false);
    const [variantTrainings, setVariantTrainings] = useState([]);
    const [newTrainingName, setNewTrainingName] = useState('');
    const [newTrainingMetadata, setNewTrainingMetadata] = useState(emptyTrainingMetadata());
    const [trainingVariants, setTrainingVariants] = useState([]);
    const [selectedVariantId, setSelectedVariantId] = useState('');
    const [exerciseTypes, setExerciseTypes] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [isCurrentTrainingLoading, setIsCurrentTrainingLoading] = useState(false);
    const [saveNotice, setSaveNotice] = useState('');
    const [saveError, setSaveError] = useState('');
    const [importValue, setImportValue] = useState('');
    const [importNotice, setImportNotice] = useState('');
    const [importError, setImportError] = useState('');
    const [isImportOpen, setIsImportOpen] = useState(false);
    const [generateNotice, setGenerateNotice] = useState('');
    const [generateReason, setGenerateReason] = useState('');
    const [generateError, setGenerateError] = useState('');
    const [isGenerateOpen, setIsGenerateOpen] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [expandedActivityIds, setExpandedActivityIds] = useState([]);
    const [selectedAthleteId, setSelectedAthleteId] = useState('');
    const [isEditingCurrentTraining, setIsEditingCurrentTraining] = useState(false);
    const [editableTraining, setEditableTraining] = useState(emptyEditableTraining());
    const [trainingActionNotice, setTrainingActionNotice] = useState('');
    const [trainingActionError, setTrainingActionError] = useState('');
    const [isUpdatingTraining, setIsUpdatingTraining] = useState(false);
    const [isDeletingTraining, setIsDeletingTraining] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { setStepDone } = useCoachOnboarding();
    const trainingRequestRef = useRef(0);
    const experimentalAiEnabled = demoMode
        ? userInfo?.experimentalAiEnabled ?? true
        : Boolean(userInfo?.experimentalAiEnabled);
    const aiGenerationEnabled = demoMode
        ? userInfo?.aiGenerationEnabled ?? true
        : Boolean(userInfo?.aiGenerationEnabled);
    const canUseAiGeneration = experimentalAiEnabled && aiGenerationEnabled;

    const clearCurrentTrainingState = useCallback(() => {
        setVariantTrainings([]);
        setCurrentTraining(undefined);
        setCurrentTrainingName('');
        setCurrentTrainingId(null);
        setCurrentTrainingLocked(false);
        setIsCurrentTrainingLoading(false);
    }, []);

    const applySelectedTraining = useCallback((training) => {
        if (!training) {
            clearCurrentTrainingState();
            return;
        }

        setCurrentTraining(training.exercises || []);
        setCurrentTrainingName(training.name || '');
        setCurrentTrainingId(training.id ?? null);
        setCurrentTrainingLocked(Boolean(training.locked));
    }, [clearCurrentTrainingState]);

    const syncVariantTrainings = useCallback((trainings, preferredTrainingId = null) => {
        const normalizedTrainings = (trainings || []).map(normalizeTrainingForList);
        setVariantTrainings(normalizedTrainings);

        if (normalizedTrainings.length === 0) {
            clearCurrentTrainingState();
            return;
        }

        const matchingTraining = preferredTrainingId !== null && preferredTrainingId !== undefined
            ? normalizedTrainings.find(
                (training) => String(training.id) === String(preferredTrainingId)
            )
            : null;
        const trainingToShow = matchingTraining || normalizedTrainings[normalizedTrainings.length - 1];
        applySelectedTraining(trainingToShow);
    }, [applySelectedTraining, clearCurrentTrainingState]);

    useEffect(() => {
        const q = searchParams.get('variant');
        if (q) {
            setSelectedVariantId(q);
        }
    }, [searchParams]);

    useEffect(() => {
        if (!demoMode) {
            return;
        }

        const variants = demoData?.trainingPlans || [];
        const selectedId = demoData?.selectedVariantId || variants[0]?.id || '';
        const demoTrainings = demoData?.trainings
            || (demoData?.currentTraining ? [normalizeTrainingForList(demoData.currentTraining)] : []);

        setTrainingVariants(variants);
        setSelectedVariantId(String(selectedId));
        setExerciseTypes(fallbackExerciseTypes);
        syncVariantTrainings(
            demoTrainings,
            demoData?.currentTraining?.id || demoTrainings[demoTrainings.length - 1]?.id || null
        );
        setUserHistory(demoData?.activity || []);
    }, [demoData, demoMode, syncVariantTrainings]);

    const fetchTrainingVariants = useCallback(async () => {
        try {
            const response = await fetch(HOST + `/v1/training_plans?userid=${userId}`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${getAuthToken()}`,
                },
            });

            if (response.status === 401) {
                navigate('/login');
                return;
            }

            if (response.ok) {
                const variantsResponse = await response.json();
                setTrainingVariants(variantsResponse.variants);
            } else {
                console.error('Failed to fetch training variants:', response.statusText);
            }
        } catch (error) {
            console.error('Failed to fetch training variants:', error);
        }
    }, [navigate, userId]);

    const fetchCurrentTraining = useCallback(async (preferredTrainingId = null) => {
        if (!selectedVariantId) {
            clearCurrentTrainingState();
            return;
        }

        const requestId = ++trainingRequestRef.current;
        setIsCurrentTrainingLoading(true);

        try {
            const response = await fetch(HOST + `/v1/training?userid=${userId}&variant=${selectedVariantId}`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${getAuthToken()}`,
                },
            });

            if (response.status === 401) {
                navigate('/login');
                return;
            }

            if (!response.ok) {
                console.error('Failed to fetch training:', response.statusText);
                return;
            }

            const data = await response.json();
            const recentTraining = data.lastTraining;
            const trainings = Array.isArray(data.trainings)
                ? data.trainings
                : (recentTraining ? [recentTraining] : []);

            if (requestId === trainingRequestRef.current) {
                syncVariantTrainings(trainings, preferredTrainingId);
                setExerciseTypes(data.exerciseTypes || []);
            }
        } catch (error) {
            console.error('Failed to fetch training:', error);
        } finally {
            if (requestId === trainingRequestRef.current) {
                setIsCurrentTrainingLoading(false);
            }
        }
    }, [clearCurrentTrainingState, navigate, selectedVariantId, syncVariantTrainings, userId]);

    const markVariantNotificationsRead = useCallback(async () => {
        if (!selectedVariantId) {
            return;
        }

        try {
            const response = await fetch(HOST + '/v1/mark_variant_notifications_read', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${getAuthToken()}`,
                },
                body: JSON.stringify({
                    userId,
                    variantId: Number(selectedVariantId),
                }),
            });

            if (response.status === 401) {
                navigate('/login');
            }
        } catch (error) {
            console.error('Failed to mark plan notifications as read:', error);
        }
    }, [navigate, selectedVariantId, userId]);

    const fetchAllUsersTraining = useCallback(async () => {
        if (!selectedVariantId) {
            setUserHistory([]);
            return;
        }

        try {
            const response = await fetch(HOST + `/v1/all_users_history?userid=${userId}&variant=${selectedVariantId}`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${getAuthToken()}`,
                },
            });

            if (response.status === 401) {
                navigate('/login');
                return;
            }

            if (response.ok) {
                const userHistoryData = await response.json();
                const filteredHistory = userHistoryData.history.filter((item) => item.completedTrainingModels.length > 0);
                setUserHistory(filteredHistory);
            } else {
                console.error('Failed to fetch user history:', response.statusText);
            }
        } catch (error) {
            console.error('Error fetching user history:', error);
        }
    }, [navigate, selectedVariantId, userId]);

    useEffect(() => {
        if (demoMode) {
            return;
        }

        fetchTrainingVariants();
    }, [demoMode, fetchTrainingVariants]);

    useEffect(() => {
        if (!currentTrainingId || !Array.isArray(currentTraining)) {
            setEditableTraining(emptyEditableTraining());
            setIsEditingCurrentTraining(false);
            setShowDeleteConfirm(false);
            return;
        }

        setEditableTraining({
            id: currentTrainingId,
            name: currentTrainingName || '',
            exercises: currentTraining.map((exercise) => ({
                id: exercise.id,
                category: exercise.category || '',
                description: exercise.description || '',
            })),
        });
    }, [currentTraining, currentTrainingId, currentTrainingName]);

    useEffect(() => {
        setTrainingActionNotice('');
        setTrainingActionError('');
        setShowDeleteConfirm(false);
        setIsEditingCurrentTraining(false);
    }, [selectedVariantId]);

    useEffect(() => {
        if (demoMode) {
            return;
        }

        markVariantNotificationsRead();
        fetchCurrentTraining();
        fetchAllUsersTraining();
    }, [demoMode, fetchAllUsersTraining, fetchCurrentTraining, markVariantNotificationsRead, selectedVariantId]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (newExercises.some((exercise) => !exercise.category || !exercise.description)) {
            alert('Please fill in all fields');
            return;
        }

        if (selectedVariantId === '') {
            alert('Please select training plan');
            return;
        }

        if (demoMode) {
            const savedTraining = {
                id: (variantTrainings[variantTrainings.length - 1]?.id || 0) + 1,
                name: newTrainingName,
                exercises: newExercises,
                locked: false,
            };

            setSaveError('');
            syncVariantTrainings([...variantTrainings, savedTraining], savedTraining.id);
            setSaveNotice('Training saved successfully.');
            setStepDone(COACH_STEPS.FIRST_TRAINING_CREATED);
            setNewExercises(emptyExercises());
            setNewTrainingName('');
            setNewTrainingMetadata(emptyTrainingMetadata());
            return;
        }

        const newPayload = {
            exercises: newExercises,
            name: newTrainingName,
            variantId: selectedVariantId,
            userId,
            ...newTrainingMetadata,
        };

        try {
            setIsSaving(true);
            setSaveError('');
            const response = await fetch(HOST + '/v1/training', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${getAuthToken()}`,
                },
                body: JSON.stringify(newPayload),
            });

            if (response.status === 401) {
                navigate('/login');
                return;
            }

            if (response.ok) {
                setStepDone(COACH_STEPS.FIRST_TRAINING_CREATED);
                setSaveNotice('Training saved successfully.');
                setNewExercises(emptyExercises());
                setNewTrainingName('');
                setNewTrainingMetadata(emptyTrainingMetadata());
                await fetchCurrentTraining();
            } else {
                setSaveNotice('');
                setSaveError('Training could not be saved. Please try again.');
                console.error('Failed to save training:', response.statusText);
            }
        } catch (error) {
            setSaveNotice('');
            setSaveError('Training could not be saved. Please try again.');
            console.error('Error saving training:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleInputChange = (index, field, value) => {
        setSaveNotice('');
        setSaveError('');
        const updatedExercises = [...newExercises];
        updatedExercises[index][field] = value;
        setNewExercises(updatedExercises);
    };

    const handleImportTraining = () => {
        if (selectedVariantId === '') {
            setImportNotice('');
            setImportError('Select a training plan before importing.');
            return;
        }

        try {
            const importedTraining = decodeImportedTraining(importValue);
            setSaveNotice('');
            setSaveError('');
            setImportError('');
            setImportNotice('Training loaded into the form. Review it and save when ready.');
            setNewTrainingName(importedTraining.name);
            setNewExercises(importedTraining.exercises);
            setNewTrainingMetadata({
                discipline: importedTraining.discipline,
                phase: importedTraining.phase,
                session_type: importedTraining.session_type,
                focus: importedTraining.focus,
                estimated_duration_minutes: importedTraining.estimated_duration_minutes,
                reason: importedTraining.reason,
            });
        } catch (error) {
            setImportNotice('');
            setImportError(error.message || 'Training import failed.');
        }
    };

    const applyTrainingToForm = (training, noticeMessage) => {
        setSaveNotice('');
        setSaveError('');
        setNewTrainingName(training.name);
        setNewExercises(training.exercises);
        setNewTrainingMetadata({
            discipline: training.discipline,
            phase: training.phase,
            session_type: training.session_type,
            focus: training.focus,
            estimated_duration_minutes: training.estimated_duration_minutes,
            reason: training.reason,
        });
        setImportError('');
        setGenerateReason('');
        setGenerateError('');
        if (noticeMessage) {
            setImportNotice('');
            setGenerateNotice(noticeMessage);
            setGenerateReason(training.reason || '');
        }
    };

    const handleGenerateTraining = async () => {
        if (selectedVariantId === '') {
            setGenerateNotice('');
            setGenerateReason('');
            setGenerateError('Select a training plan before generating.');
            return;
        }

        const selectedAiParameters = selectedVariant?.agentDescription || userInfo?.aiParameters || '';

        if (!selectedAiParameters) {
            setGenerateNotice('');
            setGenerateReason('');
            setGenerateError('Add AI training parameters to this coach AI setup or training plan before generating.');
            return;
        }

        const agentParametersResult = parseAiTrainingParameters(selectedAiParameters);
        if (!agentParametersResult.isValid) {
            setGenerateNotice('');
            setGenerateReason('');
            setGenerateError(agentParametersResult.error || 'AI training parameters are invalid.');
            return;
        }

        if (demoMode) {
            try {
                const generatedTraining = normalizeTrainingPayload({
                    name: 'DynB technique phase 2',
                    discipline: 'DynB',
                    phase: '2',
                    session_type: 'technique',
                    focus: 'Technique quality',
                    estimated_duration_minutes: 45,
                    reason: 'Demo suggestion based on stable recent completion.',
                    exercises: [
                        { category: 'Warmup', description: '2x 50m easy with relaxed pacing' },
                        { category: 'Technique', description: '4x 25m focus on line quality and glide' },
                        { category: 'Main Set', description: '3x 50m threshold with controlled recovery' },
                        { category: 'Recovery', description: '2x 50m easy loosen and breathing reset' },
                    ],
                }, 'Generated training');
                applyTrainingToForm(generatedTraining, 'Training generated and loaded into the form. Review it before saving.');
            } catch (error) {
                setGenerateNotice('');
                setGenerateReason('');
                setGenerateError(error.message || 'Training generation failed.');
            }
            return;
        }

        setIsGenerating(true);
        setGenerateNotice('');
        setGenerateReason('');
        setGenerateError('');

        try {
            const response = await fetch(`${HOST}/v1/generate_training`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${getAuthToken()}`,
                },
                body: JSON.stringify({
                    userId,
                    variantId: Number(selectedVariantId),
                }),
            });

            if (response.status === 401) {
                navigate('/login');
                return;
            }

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data?.message || 'Training generation failed.');
            }

            const generatedTraining = normalizeTrainingPayload(data.result, 'Generated training');
            applyTrainingToForm(generatedTraining, 'Training generated and loaded into the form. Review it before saving.');
        } catch (error) {
            setGenerateNotice('');
            setGenerateReason('');
            setGenerateError(error.message || 'Training generation failed.');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleEditableTrainingNameChange = (value) => {
        setTrainingActionNotice('');
        setTrainingActionError('');
        setEditableTraining((prevState) => ({
            ...prevState,
            name: value,
        }));
    };

    const handleEditableTrainingDescriptionChange = (exerciseId, value) => {
        setTrainingActionNotice('');
        setTrainingActionError('');
        setEditableTraining((prevState) => ({
            ...prevState,
            exercises: prevState.exercises.map((exercise) =>
                exercise.id === exerciseId
                    ? { ...exercise, description: value }
                    : exercise
            ),
        }));
    };

    const handleEditCurrentTraining = () => {
        setTrainingActionNotice('');
        setTrainingActionError('');
        setShowDeleteConfirm(false);
        setIsEditingCurrentTraining(true);
    };

    const handleCancelEditingCurrentTraining = () => {
        setTrainingActionNotice('');
        setTrainingActionError('');
        setIsEditingCurrentTraining(false);
        setEditableTraining({
            id: currentTrainingId,
            name: currentTrainingName || '',
            exercises: (currentTraining || []).map((exercise) => ({
                id: exercise.id,
                category: exercise.category || '',
                description: exercise.description || '',
            })),
        });
    };

    const handleUpdateCurrentTraining = async () => {
        const trimmedName = editableTraining.name.trim();
        const hasMissingDescription = editableTraining.exercises.some(
            (exercise) => !exercise.description || !exercise.description.trim()
        );

        if (!trimmedName) {
            setTrainingActionError('Training title is required.');
            return;
        }

        if (hasMissingDescription) {
            setTrainingActionError('Each exercise needs a description.');
            return;
        }

        if (!editableTraining.id) {
            setTrainingActionError('No training selected for editing.');
            return;
        }

        if (demoMode) {
            const updatedExercises = editableTraining.exercises.map((exercise) => ({
                id: exercise.id,
                category: exercise.category,
                description: exercise.description.trim(),
            }));

            const updatedTrainings = variantTrainings.map((training) =>
                training.id === editableTraining.id
                    ? {
                        ...training,
                        name: trimmedName,
                        exercises: updatedExercises,
                    }
                    : training
            );
            syncVariantTrainings(updatedTrainings, editableTraining.id);
            setTrainingActionNotice('Training updated successfully.');
            setTrainingActionError('');
            setIsEditingCurrentTraining(false);
            return;
        }

        setIsUpdatingTraining(true);
        setTrainingActionNotice('');
        setTrainingActionError('');

        try {
            const payload = {
                userId,
                trainingId: editableTraining.id,
                name: trimmedName,
                exercises: editableTraining.exercises.map((exercise) => ({
                    id: exercise.id,
                    description: exercise.description.trim(),
                })),
            };

            const response = await fetch(`${HOST}/v1/training`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${getAuthToken()}`,
                },
                body: JSON.stringify(payload),
            });

            const responseBody = await response.json();

            if (!response.ok) {
                setTrainingActionError(responseBody?.message || 'Failed to update the training.');
                return;
            }

            setTrainingActionNotice('Training updated successfully.');
            setIsEditingCurrentTraining(false);
            await fetchCurrentTraining();
            await fetchAllUsersTraining();
        } catch (error) {
            console.error('Failed to update training:', error);
            setTrainingActionError('Failed to update the training.');
        } finally {
            setIsUpdatingTraining(false);
        }
    };

    const handleDeleteCurrentTraining = async () => {
        if (!currentTrainingId) {
            setTrainingActionError('No training selected for deletion.');
            return;
        }

        if (demoMode) {
            const remainingTrainings = variantTrainings.filter(
                (training) => training.id !== currentTrainingId
            );
            syncVariantTrainings(remainingTrainings);
            setEditableTraining(emptyEditableTraining());
            setShowDeleteConfirm(false);
            setTrainingActionNotice('Training deleted successfully.');
            setTrainingActionError('');
            return;
        }

        setIsDeletingTraining(true);
        setTrainingActionNotice('');
        setTrainingActionError('');

        try {
            const response = await fetch(`${HOST}/v1/training`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${getAuthToken()}`,
                },
                body: JSON.stringify({
                    userId,
                    trainingId: currentTrainingId,
                }),
            });

            const responseBody = await response.json();

            if (!response.ok) {
                setTrainingActionError(responseBody?.message || 'Failed to delete the training.');
                return;
            }

            setTrainingActionNotice('Training deleted successfully.');
            setShowDeleteConfirm(false);
            setIsEditingCurrentTraining(false);
            await fetchCurrentTraining();
            await fetchAllUsersTraining();
        } catch (error) {
            console.error('Failed to delete training:', error);
            setTrainingActionError('Failed to delete the training.');
        } finally {
            setIsDeletingTraining(false);
        }
    };

    const handleTrainingVariantChange = (e) => {
        setSaveNotice('');
        setSaveError('');
        setImportNotice('');
        setImportError('');
        setGenerateNotice('');
        setGenerateReason('');
        setGenerateError('');
        setSelectedVariantId(e.target.value);
    };

    const handleViewedTrainingChange = async (trainingId) => {
        const nextTraining = variantTrainings.find(
            (training) => String(training.id) === String(trainingId)
        );

        if (!nextTraining || String(nextTraining.id) === String(currentTrainingId)) {
            return;
        }

        setTrainingActionNotice('');
        setTrainingActionError('');
        setShowDeleteConfirm(false);
        setIsEditingCurrentTraining(false);

        if (demoMode) {
            setIsCurrentTrainingLoading(true);
            window.setTimeout(() => {
                applySelectedTraining(nextTraining);
                setIsCurrentTrainingLoading(false);
            }, 120);
            return;
        }

        await fetchCurrentTraining(trainingId);
    };

    const addNewExercise = () => {
        setSaveNotice('');
        setSaveError('');
        setNewExercises([...newExercises, { category: '', description: '' }]);
    };

    const removeExercise = (index) => {
        setSaveNotice('');
        setSaveError('');
        const updatedExercises = [...newExercises];
        updatedExercises.splice(index, 1);
        setNewExercises(updatedExercises);
    };

    const toggleActivityHistory = (activityId) => {
        setExpandedActivityIds((prevState) =>
            prevState.includes(activityId)
                ? prevState.filter((id) => id !== activityId)
                : [...prevState, activityId]
        );
    };

    const selectedVariant = useMemo(
        () => trainingVariants.find((variant) => String(variant.id) === String(selectedVariantId)),
        [selectedVariantId, trainingVariants]
    );
    const selectedAiParameters = selectedVariant?.agentDescription || userInfo?.aiParameters || '';

    const recentActivity = useMemo(() => {
        return userHistory.map((user) => {
            const trainings = user.completedTrainingModels || [];
            const latestTraining = trainings[0];
            const sessions = trainings.map((training) => {
                const exercises = training.exerciseHistoryModels || [];
                const completedCount = exercises.filter((item) => item.completed === 1).length;

                return {
                    id: `${training.trainingId || training.timestamp}-${training.timestamp}`,
                    trainingId: training.trainingId ?? null,
                    name: training.name || 'Training session',
                    timestamp: training.timestamp,
                    completedCount,
                    totalCount: exercises.length,
                    exercises: exercises.map((exercise, index) => ({
                        id: `${training.trainingId || training.timestamp}-${index}`,
                        category: exercise.category || 'Exercise',
                        description: exercise.description || 'No description',
                        completed: exercise.completed === 1,
                        comment: exercise.comment || '',
                        commentLabel: user.userInfo
                            ? `${user.userInfo.first_name} ${user.userInfo.last_name} comment`
                            : `${user.firstName} ${user.lastName} comment`,
                    })),
                };
            });

            if (user.userInfo) {
                const completedCount = sessions.reduce((acc, training) => acc + training.completedCount, 0);
                const totalCount = sessions.reduce((acc, training) => acc + training.totalCount, 0);

                return {
                    id: user.userInfo.id || `${user.userInfo.first_name}-${user.userInfo.last_name}`,
                    name: `${user.userInfo.first_name} ${user.userInfo.last_name}`,
                    latestTimestamp: latestTraining?.timestamp,
                    completedCount,
                    totalCount,
                    trainingCount: trainings.length,
                    planName: user.variantName || selectedVariant?.name || '',
                    sessions,
                };
            }

            const completedCount = sessions.reduce((acc, training) => acc + training.completedCount, 0);
            const totalCount = sessions.reduce((acc, training) => acc + training.totalCount, 0);

            return {
                id: user.userId || `${user.firstName}-${user.lastName}`,
                name: `${user.firstName} ${user.lastName}`,
                latestTimestamp: latestTraining?.timestamp || user.completedTrainingModels?.[0]?.timestamp,
                completedCount,
                totalCount,
                trainingCount: trainings.length,
                planName: user.variantName || selectedVariant?.name || '',
                sessions,
            };
        });
    }, [selectedVariant?.name, userHistory]);

    const showInlineSingleSubscriberHistory = Boolean(
        selectedVariant?.private && Number(selectedVariant?.subscriberCount || 0) === 1
    );
    const singleSubscriberHistory = showInlineSingleSubscriberHistory ? (recentActivity[0] || null) : null;
    const singleSubscriberCurrentSession = useMemo(() => {
        if (!singleSubscriberHistory || !currentTrainingId) {
            return null;
        }

        return singleSubscriberHistory.sessions.find(
            (session) => String(session.trainingId) === String(currentTrainingId)
        ) || null;
    }, [currentTrainingId, singleSubscriberHistory]);

    useEffect(() => {
        if (recentActivity.length === 0) {
            setSelectedAthleteId('');
            return;
        }

        const stillExists = recentActivity.some((entry) => String(entry.id) === String(selectedAthleteId));
        if (!stillExists) {
            setSelectedAthleteId(String(recentActivity[0].id));
        }
    }, [recentActivity, selectedAthleteId]);

    return (
        <div className={styles.adminContainer} aria-busy={isSaving}>
            {isSaving && (
                <div className={styles.loadingOverlay} role="status" aria-live="polite" aria-label="Saving training, please wait…">
                    <div className={styles.spinner} />
                    <span className={styles.loadingText}>Saving…</span>
                </div>
            )}

            <section className={styles.statsGrid}>
                <article className={styles.statCard}>
                    <span>Training plans</span>
                    <strong>{trainingVariants.length}</strong>
                    <small>Plans available to build into</small>
                </article>
                <article className={styles.statCard}>
                    <span>Exercises in draft</span>
                    <strong>{newExercises.length}</strong>
                    <small>Rows in the training you are composing</small>
                </article>
                <article className={styles.statCard}>
                    <span>Recent athlete activity</span>
                    <strong>{recentActivity.length}</strong>
                    <small>Athletes with recorded training history</small>
                </article>
            </section>

            <section className={styles.trainingGrid}>
                <article className={`${styles.panel} ${styles.panelWide}`}>
                    <div className={styles.panelHeader}>
                        <div>
                            <span className={styles.sectionLabel}>Training builder</span>
                            <h3>Create the next session</h3>
                            <p>
                                Pick the plan you want to work on, name the session clearly, then define each exercise with a category and a concrete description.
                            </p>
                        </div>
                    </div>

                    <div className={styles.workspaceBar}>
                        <label htmlFor="variantSelect" className={styles.componentLabel}>Training plan</label>
                        <select
                            id="variantSelect"
                            className={styles.variantSelect}
                            value={selectedVariantId}
                            onChange={handleTrainingVariantChange}
                        >
                            <option value="">Select a plan</option>
                            {trainingVariants.map((variant) => (
                                <option key={variant.id} value={variant.id}>
                                    {variant.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {selectedVariantId !== '' && (
                        <>
                            {selectedVariant?.description ? (
                                <div className={styles.selectedPlanCard}>
                                    <strong>Training plan description</strong>
                                    <p>{selectedVariant.description}</p>
                                </div>
                            ) : null}

                            {SHOW_IMPORT_TRAINING_PANEL ? (
                                <div className={styles.importPanel}>
                                    <button
                                        type="button"
                                        className={styles.importToggle}
                                        onClick={() => setIsImportOpen((value) => !value)}
                                        aria-expanded={isImportOpen}
                                    >
                                        <div>
                                            <div className={styles.importPills}>
                                                <span className={styles.sectionLabel}>Import training</span>
                                                <span className={styles.experimentalPill}>Experimental feature</span>
                                            </div>
                                            <h4>{isImportOpen ? 'Paste a base64 training string' : 'Load a pasted training into the form'}</h4>
                                            <p>
                                                {isImportOpen
                                                    ? 'We will decode it and fill the form below so you can review before saving.'
                                                    : 'Click to expand and paste a base64 training payload.'}
                                            </p>
                                        </div>
                                        <span className={`${styles.importChevron} ${isImportOpen ? styles.importChevronOpen : ''}`}>
                                            ▾
                                        </span>
                                    </button>

                                    {isImportOpen ? (
                                        <>
                                            <div className={styles.fieldGroup}>
                                                <label htmlFor="trainingImport" className={styles.componentLabel}>Encoded training</label>
                                                <textarea
                                                    id="trainingImport"
                                                    value={importValue}
                                                    onChange={(e) => {
                                                        setImportNotice('');
                                                        setImportError('');
                                                        setImportValue(e.target.value);
                                                    }}
                                                    placeholder="Paste the base64-encoded training payload here"
                                                    className={styles.importInput}
                                                />
                                            </div>

                                            <div className={styles.importActions}>
                                                <button type="button" className={styles.addButton} onClick={handleImportTraining}>
                                                    Load into form
                                                </button>
                                            </div>

                                            {importNotice ? (
                                                <p className={styles.formNoticeSuccess}>{importNotice}</p>
                                            ) : null}

                                            {importError ? (
                                                <p className={styles.formNoticeError}>{importError}</p>
                                            ) : null}
                                        </>
                                    ) : null}
                                </div>
                            ) : null}

                            {canUseAiGeneration ? (
                                <div className={styles.importPanel}>
                                    <button
                                        type="button"
                                        className={styles.importToggle}
                                        onClick={() => setIsGenerateOpen((value) => !value)}
                                        aria-expanded={isGenerateOpen}
                                    >
                                        <div>
                                            <div className={styles.importPills}>
                                                <span className={styles.sectionLabel}>Generate training</span>
                                                <span className={styles.experimentalPill}>Experimental feature</span>
                                            </div>
                                            <h4>{isGenerateOpen ? 'Generate a suggested training' : 'Generate a training from the plan agent'}</h4>
                                            <p>
                                                {isGenerateOpen
                                                    ? 'Uses this plan’s AI training parameters, or your coach-level parameters when the plan has none, plus recent completed sessions.'
                                                    : 'Click to generate a suggested training for the selected plan.'}
                                            </p>
                                        </div>
                                        <span className={`${styles.importChevron} ${isGenerateOpen ? styles.importChevronOpen : ''}`}>
                                            ▾
                                        </span>
                                    </button>

                                    {isGenerateOpen ? (
                                        <>
                                            <div className={styles.fieldGroup}>
                                                <label className={styles.componentLabel}>AI training parameters</label>
                                                <div className={styles.selectedPlanCard}>
                                                    <strong>{selectedVariant?.name || 'Selected plan'}</strong>
                                                    <p>{selectedAiParameters || 'No AI training parameters yet.'}</p>
                                                </div>
                                            </div>

                                            <div className={styles.importActions}>
                                                <button
                                                    type="button"
                                                    className={styles.addButton}
                                                    onClick={handleGenerateTraining}
                                                    disabled={isGenerating}
                                                >
                                                    {isGenerating ? 'Generating…' : 'Generate training'}
                                                </button>
                                            </div>

                                            {generateNotice ? (
                                                <div className={styles.formNoticeSuccess}>
                                                    <p>{generateNotice}</p>
                                                    {generateReason ? <p>{generateReason}</p> : null}
                                                </div>
                                            ) : null}

                                            {generateError ? (
                                                <p className={styles.formNoticeError}>{generateError}</p>
                                            ) : null}
                                        </>
                                    ) : null}
                                </div>
                            ) : null}

                            <form className={styles.adminForm} onSubmit={handleSubmit}>
                                <div className={styles.fieldGroup}>
                                    <label htmlFor="trainingName" className={styles.componentLabel}>Training name</label>
                                    <input
                                        id="trainingName"
                                        type="text"
                                        className={styles.nameInput}
                                        placeholder="Week 7 threshold session"
                                        value={newTrainingName}
                                        onChange={(e) => {
                                            setSaveNotice('');
                                            setSaveError('');
                                            setNewTrainingName(e.target.value);
                                        }}
                                        required
                                    />
                                </div>

                                <div className={styles.exerciseStack}>
                                    {newExercises.map((exercise, index) => (
                                        <article key={index} className={styles.exerciseCard}>
                                            <div className={styles.exerciseCardHeader}>
                                                <div className={styles.exerciseIndex}>{index + 1}</div>
                                                <div>
                                                    <strong>Exercise {index + 1}</strong>
                                                    <p>Define the category and execution details clearly.</p>
                                                </div>
                                                {newExercises.length > 1 && (
                                                    <button
                                                        type="button"
                                                        className={styles.removeButton}
                                                        onClick={() => removeExercise(index)}
                                                    >
                                                        Remove
                                                    </button>
                                                )}
                                            </div>

                                            <div className={styles.exerciseFields}>
                                                <div className={styles.fieldGroup}>
                                                    <label className={styles.componentLabel}>Category</label>
                                                    <AutocompleteInput
                                                        exerciseTypes={exerciseTypes}
                                                        index={index}
                                                        field="category"
                                                        value={newExercises[index].category}
                                                        handleInputChange={handleInputChange}
                                                    />
                                                </div>

                                                <div className={styles.fieldGroup}>
                                                    <label className={styles.componentLabel}>Description</label>
                                                    <textarea
                                                        value={exercise.description}
                                                        onChange={(e) => handleInputChange(index, 'description', e.target.value)}
                                                        placeholder="3x 62.5m with 2:00 recovery and technique focus"
                                                        className={styles.descriptionInput}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        </article>
                                    ))}
                                </div>

                                <div className={styles.buttonContainer}>
                                    <button type="button" className={styles.addButton} onClick={addNewExercise}>
                                        Add exercise
                                    </button>

                                    <button type="submit" className={styles.submitButton} disabled={isSaving}>
                                        {isSaving ? 'Saving…' : 'Save training'}
                                    </button>
                                </div>

                                {saveNotice ? (
                                    <p className={styles.formNoticeSuccess}>{saveNotice}</p>
                                ) : null}

                                {saveError ? (
                                    <p className={styles.formNoticeError}>{saveError}</p>
                                ) : null}
                            </form>
                        </>
                    )}
                </article>

                {selectedVariantId ? (
                <aside className={styles.sideColumn}>
                    <article className={styles.panel}>
                        <div className={styles.panelHeader}>
                            <div>
                                <span className={styles.sectionLabel}>Latest training</span>
                                <h3>{currentTrainingName || 'No session saved yet'}</h3>
                                <p>
                                    Browse all saved trainings inside the selected plan.
                                </p>
                            </div>
                        </div>

                        {isCurrentTrainingLoading ? (
                            <div className={styles.inlineLoadingState} role="status" aria-live="polite">
                                <div className={styles.spinner} />
                                <span>Loading training…</span>
                            </div>
                        ) : null}

                        {variantTrainings.length > 0 ? (
                            <div className={styles.trainingPager}>
                                {variantTrainings.map((training, index) => (
                                    <button
                                        key={training.id || index}
                                        type="button"
                                        className={`${styles.trainingPagerLink} ${
                                            String(training.id) === String(currentTrainingId)
                                                ? styles.trainingPagerLinkActive
                                                : ''
                                        }`}
                                        onClick={() => handleViewedTrainingChange(training.id)}
                                        disabled={isCurrentTrainingLoading}
                                    >
                                        {index + 1}
                                    </button>
                                ))}
                            </div>
                        ) : null}

                        {trainingActionNotice ? (
                            <p className={styles.formNoticeSuccess}>{trainingActionNotice}</p>
                        ) : null}

                        {trainingActionError ? (
                            <p className={styles.formNoticeError}>{trainingActionError}</p>
                        ) : null}

                        {!currentTraining || currentTraining.length === 0 ? (
                            <div className={styles.emptyCard}>
                                <strong>No saved training yet.</strong>
                                <p>Once you save a session, it will appear here as a quick preview.</p>
                            </div>
                        ) : (
                            <div className={styles.latestTrainingEditor}>
                                {!singleSubscriberCurrentSession ? (
                                    <div className={styles.latestTrainingHeader}>
                                        {isEditingCurrentTraining ? (
                                            <input
                                                type="text"
                                                className={styles.latestTrainingTitleInput}
                                                value={editableTraining.name}
                                                onChange={(event) => handleEditableTrainingNameChange(event.target.value)}
                                                placeholder="Training title"
                                            />
                                        ) : (
                                            <strong className={styles.latestTrainingTitle}>
                                                {currentTrainingName || 'Latest training'}
                                            </strong>
                                        )}
                                    </div>
                                ) : null}

                                {!currentTrainingLocked && currentTrainingId ? (
                                    <div className={styles.latestTrainingActions}>
                                        {isEditingCurrentTraining ? (
                                            <>
                                                <button
                                                    type="button"
                                                    className={styles.secondaryInlineButton}
                                                    onClick={handleCancelEditingCurrentTraining}
                                                    disabled={isUpdatingTraining}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="button"
                                                    className={styles.primaryInlineButton}
                                                    onClick={handleUpdateCurrentTraining}
                                                    disabled={isUpdatingTraining}
                                                >
                                                    {isUpdatingTraining ? 'Saving…' : 'Save'}
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    type="button"
                                                    className={styles.secondaryInlineButton}
                                                    onClick={handleEditCurrentTraining}
                                                    disabled={isCurrentTrainingLoading}
                                                >
                                                    Edit
                                                </button>
                                                <div className={styles.deleteActionWrap}>
                                                    <button
                                                        type="button"
                                                        className={styles.dangerInlineButton}
                                                        onClick={() => {
                                                            setTrainingActionNotice('');
                                                            setTrainingActionError('');
                                                            setShowDeleteConfirm((value) => !value);
                                                        }}
                                                        disabled={isCurrentTrainingLoading}
                                                    >
                                                        Delete
                                                    </button>
                                                    {showDeleteConfirm ? (
                                                        <div className={styles.deleteConfirmCard}>
                                                            <strong>Are you sure you want to delete this training?</strong>
                                                            <div className={styles.deleteConfirmActions}>
                                                                <button
                                                                    type="button"
                                                                    className={styles.dangerInlineButton}
                                                                    onClick={handleDeleteCurrentTraining}
                                                                    disabled={isDeletingTraining}
                                                                >
                                                                    {isDeletingTraining ? 'Deleting…' : 'Yes'}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className={styles.secondaryInlineButton}
                                                                    onClick={() => setShowDeleteConfirm(false)}
                                                                    disabled={isDeletingTraining}
                                                                >
                                                                    No
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ) : !singleSubscriberCurrentSession ? (
                                    <div className={styles.latestTrainingMetaRow}>
                                        <span className={styles.trainingLockedNote}>
                                            Completed trainings can no longer be edited or deleted.
                                        </span>
                                    </div>
                                ) : null}

                                {singleSubscriberCurrentSession ? (
                                    <div className={styles.sessionList}>
                                        <article className={styles.sessionCard}>
                                            <div className={styles.sessionHeader}>
                                                <div>
                                                    <strong>{singleSubscriberCurrentSession.name}</strong>
                                                    <span>
                                                        {singleSubscriberCurrentSession.timestamp
                                                            ? new Date(singleSubscriberCurrentSession.timestamp).toLocaleString()
                                                            : 'Session timestamp unavailable'}
                                                    </span>
                                                </div>
                                                <span className={`${styles.sessionSummary} ${styles.sessionCompletePill}`}>
                                                    {singleSubscriberCurrentSession.completedCount}/{singleSubscriberCurrentSession.totalCount} completed
                                                </span>
                                            </div>

                                            <div className={styles.sessionExerciseList}>
                                                {singleSubscriberCurrentSession.exercises.map((exercise) => (
                                                    <div
                                                        key={exercise.id}
                                                        className={`${styles.sessionExerciseRow} ${
                                                            exercise.completed
                                                                ? styles.sessionExerciseCompleted
                                                                : styles.sessionExerciseFailed
                                                        }`}
                                                    >
                                                        <div className={styles.sessionExerciseCopy}>
                                                            <strong>{getExerciseCategoryLabel(exercise.category)}</strong>
                                                            <span>{exercise.description}</span>
                                                            {exercise.comment ? (
                                                                <div className={styles.sessionExerciseComment}>
                                                                    <small>{exercise.commentLabel || 'Athlete comment'}</small>
                                                                    <p>{exercise.comment}</p>
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                        <span
                                                            className={`${styles.sessionExerciseStatus} ${
                                                                exercise.completed
                                                                    ? styles.sessionExerciseStatusCompleted
                                                                    : styles.sessionExerciseStatusFailed
                                                            }`}
                                                        >
                                                            {exercise.completed ? 'Completed' : 'Failed'}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </article>
                                    </div>
                                ) : (
                                    <div className={styles.previewList}>
                                        {(isEditingCurrentTraining ? editableTraining.exercises : currentTraining).map((exercise, index) => (
                                            <div key={`${exercise.id || exercise.category}-${index}`} className={styles.previewRow}>
                                            <div className={styles.previewIndex}>{index + 1}</div>
                                            <div className={styles.previewCopy}>
                                                <strong>{getExerciseCategoryLabel(exercise.category)}</strong>
                                                {isEditingCurrentTraining ? (
                                                    <textarea
                                                        className={styles.latestTrainingDescriptionInput}
                                                        value={exercise.description}
                                                        onChange={(event) => handleEditableTrainingDescriptionChange(exercise.id, event.target.value)}
                                                    />
                                                ) : (
                                                    <span>{exercise.description}</span>
                                                )}
                                            </div>
                                        </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </article>

                    {!showInlineSingleSubscriberHistory ? (
                    <article className={styles.panel}>
                        <div className={styles.panelHeader}>
                            <div>
                                <span className={styles.sectionLabel}>Athlete activity</span>
                                <h3>See who is moving through the work.</h3>
                                <p>
                                    A quick scan of how subscribed athletes are completing the sessions inside this plan.
                                </p>
                            </div>
                        </div>

                        {selectedVariant ? (
                            <div className={styles.selectedPlanCard}>
                                <strong>Viewing activity for {selectedVariant.name}</strong>
                                <p>This section is scoped to the currently selected training plan.</p>
                            </div>
                        ) : null}

                        {recentActivity.length === 0 ? (
                            <div className={styles.emptyCard}>
                                <strong>No athlete history yet.</strong>
                                <p>Completed sessions will appear here once athletes start logging results.</p>
                            </div>
                        ) : (
                            <div className={styles.activityList}>
                                {recentActivity.map((entry) => (
                                    <article key={entry.id} className={styles.activityCard}>
                                        <div className={styles.activityHeader}>
                                            <div>
                                                <strong>{entry.name}</strong>
                                                <span>{entry.planName || 'Selected training plan'}</span>
                                            </div>
                                            <span>{entry.latestTimestamp ? new Date(entry.latestTimestamp).toLocaleString() : 'No timestamp'}</span>
                                        </div>
                                        <div className={styles.activityMeta}>
                                            <span>{entry.trainingCount} recorded sessions</span>
                                            <span>{entry.completedCount}/{entry.totalCount} exercises completed</span>
                                        </div>
                                        <button
                                            type="button"
                                            className={styles.activityToggle}
                                            onClick={() => toggleActivityHistory(entry.id)}
                                        >
                                            {expandedActivityIds.includes(entry.id) ? 'Hide session history' : 'View session history'}
                                        </button>

                                        {expandedActivityIds.includes(entry.id) ? (
                                            <div className={styles.sessionList}>
                                                {entry.sessions.map((session) => (
                                                    <article key={session.id} className={styles.sessionCard}>
                                                        <div className={styles.sessionHeader}>
                                                            <div>
                                                                <strong>{session.name}</strong>
                                                                <span>
                                                                    {session.timestamp
                                                                        ? new Date(session.timestamp).toLocaleString()
                                                                        : 'Session timestamp unavailable'}
                                                                </span>
                                                            </div>
                                                            <span className={`${styles.sessionSummary} ${styles.sessionCompletePill}`}>
                                                                {session.completedCount}/{session.totalCount} completed
                                                            </span>
                                                        </div>

                                                        <div className={styles.sessionExerciseList}>
                                                            {session.exercises.map((exercise) => (
                                                                <div
                                                                    key={exercise.id}
                                                                    className={`${styles.sessionExerciseRow} ${
                                                                        exercise.completed
                                                                            ? styles.sessionExerciseCompleted
                                                                            : styles.sessionExerciseFailed
                                                                    }`}
                                                                >
                                                                    <div className={styles.sessionExerciseCopy}>
                                                                        <strong>{getExerciseCategoryLabel(exercise.category)}</strong>
                                                                        <span>{exercise.description}</span>
                                                                        {exercise.comment ? (
                                                                            <div className={styles.sessionExerciseComment}>
                                                                                <small>{exercise.commentLabel || 'Athlete comment'}</small>
                                                                                <p>{exercise.comment}</p>
                                                                            </div>
                                                                        ) : null}
                                                                    </div>
                                                                    <span
                                                                        className={`${styles.sessionExerciseStatus} ${
                                                                            exercise.completed
                                                                                ? styles.sessionExerciseStatusCompleted
                                                                                : styles.sessionExerciseStatusFailed
                                                                        }`}
                                                                    >
                                                                        {exercise.completed ? 'Completed' : 'Failed'}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </article>
                                                ))}
                                            </div>
                                        ) : null}
                                    </article>
                                ))}
                            </div>
                        )}
                    </article>
                    ) : null}
                </aside>
                ) : null}
            </section>
        </div>
    );
};

export default Admin;
